import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

// Initial state
const initialState = {
    listData: null,
    page: 0,
    pageCount: 0,
    loading: false,
    summary: {
        total_credit_topup: 0,
        total_credit_spent: 0,
        total_treasure_earned: 0,
        total_treasure_spent: 0,
        total_concept_earned: 0,
        total_concept_spent: 0,
        total_vip_earned: 0,
        total_vip_deducted: 0,
    },
    detailData: null,
    detailLoading: false,
};

// Slice
const transactionHistorySlice = createSlice({
    name: "transactionHistory",
    initialState,
    reducers: {
        // Get transaction history list
        getTransactionHistoryRequest: (state) => {
            state.loading = true;
        },
        getTransactionHistorySuccess: (state, action) => {
            state.loading = false;
            state.listData = action.payload.data;
            state.page = action.payload.current_page;
            state.pageCount = action.payload.page_count;
            state.summary = action.payload.summary || initialState.summary;
        },
        getTransactionHistoryFail: (state) => {
            state.loading = false;
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.summary = initialState.summary;
        },

        // Get transaction detail
        getTransactionDetailRequest: (state) => {
            state.detailLoading = true;
        },
        getTransactionDetailSuccess: (state, action) => {
            state.detailLoading = false;
            state.detailData = action.payload;
        },
        getTransactionDetailFail: (state) => {
            state.detailLoading = false;
            state.detailData = null;
        },

        // Clear detail data
        clearTransactionDetail: (state) => {
            state.detailData = null;
        },
    },
});

// Export actions
export const {
    getTransactionHistoryRequest,
    getTransactionHistorySuccess,
    getTransactionHistoryFail,
    getTransactionDetailRequest,
    getTransactionDetailSuccess,
    getTransactionDetailFail,
    clearTransactionDetail,
} = transactionHistorySlice.actions;

// Thunks

/**
 * Fetch transaction history list with filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.transaction_category - Filter by category (wallet_credit/point)
 * @param {string} params.point_type - Filter by point type (treasure/concept/vip)
 * @param {string} params.transaction_type - Filter by type (credit/debit/topup/purchase/earn/deduct/exchange)
 * @param {string} params.customer_search - Search customer by name/email/phone
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {string} params.related_table - Filter by related table
 */
export const doGetTransactionHistory = (params) => async (dispatch) => {
    dispatch(getTransactionHistoryRequest());

    try {
        const token = localStorage.getItem("token");
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {
                page: params.page || 1,
                size: params.limit || 15,
                ...params,
            },
        };

        // Remove undefined/null parameters
        Object.keys(config.params).forEach((key) => {
            if (
                config.params[key] === undefined ||
                config.params[key] === null ||
                config.params[key] === "" ||
                key === "limit" // Remove limit since we use 'size' instead
            ) {
                delete config.params[key];
            }
        });

        const response = await axios.get(
            `/api/admin/transaction-history`,
            config
        );

        if (response.data.message === "SUCCESS") {
            dispatch(
                getTransactionHistorySuccess({
                    data: response.data.data,
                    current_page: response.data.current_page,
                    page_count: response.data.page_count,
                    summary: response.data.summary,
                })
            );
        } else {
            dispatch(getTransactionHistoryFail());
        }
    } catch (error) {
        console.error("Error fetching transaction history:", error);
        dispatch(getTransactionHistoryFail());
    }
};

/**
 * Fetch transaction detail
 * @param {number} transactionId - Transaction ID
 */
export const doGetTransactionDetail = (transactionId) => async (dispatch) => {
    dispatch(getTransactionDetailRequest());

    try {
        const token = localStorage.getItem("token");
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        const response = await axios.get(
            `/api/admin/transaction-history/${transactionId}`,
            config
        );

        if (response.data.message === "SUCCESS") {
            dispatch(getTransactionDetailSuccess(response.data.data));
        } else {
            dispatch(getTransactionDetailFail());
        }
    } catch (error) {
        console.error("Error fetching transaction detail:", error);
        dispatch(getTransactionDetailFail());
    }
};

// Selectors
export const getTransactionHistoryList = (state) =>
    state.transactionHistory.listData;
export const getTransactionHistoryPage = (state) =>
    state.transactionHistory.page;
export const getTransactionHistoryPageCount = (state) =>
    state.transactionHistory.pageCount;
export const getTransactionHistoryLoading = (state) =>
    state.transactionHistory.loading;
export const getTransactionHistorySummary = (state) =>
    state.transactionHistory.summary;
export const getTransactionDetailData = (state) =>
    state.transactionHistory.detailData;
export const getTransactionDetailLoading = (state) =>
    state.transactionHistory.detailLoading;

// Export reducer
export default transactionHistorySlice.reducer;
